import { MediaObserver } from '@angular/flex-layout';
import { AwsUtil } from './../../authentication/aws.service';
import { ServiceRegistry } from './../../service/service-registry.service';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { AmChartsService } from '@amcharts/amcharts3-angular';
import { AppConfig } from './../../app.config';
import { Observable, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { OnInit, ChangeDetectorRef } from '@angular/core';
import { Component, Input } from '@angular/core';
import { SHLadderComponent } from './s-h-ladder.component';
import { SHLadderConfig } from './s-h-ladder-config';
import { SurvivalHorizonService } from './../survival-horizon.service';
import { SHLadderSelection } from './s-h-ladder-selection';
import {
    GetSurvivalHorizonServiceDataOutput,
    SurvivalHorizonData,
    generateEventDatetime,
    CurrencyUtil,
} from '@liquitics/transaction';
import * as moment from 'moment';

@Component({
    selector: 'app-s-h-compare-ladder',
    templateUrl: './s-h-ladder.component.html',
    styleUrls: ['./s-h-ladder-base.component.scss', './s-h-compare-ladder.component.scss']
})
export class SHCompareLadderComponent extends SHLadderComponent implements OnInit {
    @Input() currency;
    private previousAsOfDatetime: moment.Moment;
    private compareHeader = '(Snapshot)';
    private liveHeader: string;

    constructor(protected survivalHorizonService: SurvivalHorizonService,
        protected appConfig: AppConfig,
        protected amCharts: AmChartsService,
        protected dashboardEventService: DashboardEventService,
        protected serviceRegistry: ServiceRegistry,
        protected awsUtil: AwsUtil,
        protected changeDetectorRef: ChangeDetectorRef,
        protected media: MediaObserver) {
        super(survivalHorizonService, appConfig, amCharts, dashboardEventService, serviceRegistry, awsUtil, changeDetectorRef, media);
    }

    protected initConfiguration(): SHLadderConfig {
        const configuration = new SHLadderConfig();
        configuration.blockKeyExtractor = (survivalHorizonData: SurvivalHorizonData) => {
            let key = survivalHorizonData.currency;
            if (survivalHorizonData.rowKey) {
                if (survivalHorizonData.rowKey.indexOf('(Snapshot)') > 0) {
                    key += '(Snapshot)';
                } else if (survivalHorizonData.rowKey.indexOf('(Diff)') > 0) {
                    key += '(Diff)';
                }
            }
            return key;
        };
        configuration.blockRetriever = () => {
            return Array.of(this.currency, this.currency + '(Snapshot)', this.currency + '(Diff)');
        };
        configuration.chartBlockRetriever = () => {
            return Array.of(this.currency + '(Diff)');
        };
        configuration.liveStreaming = false;
        configuration.rowSelectionHandler = (selection: SHLadderSelection) => {
            selection.selectedCurrency = selection.selectedCurrency.substring(0, 3);
            this.dashboardEventService.compareLadderRowSelectionSubject.next(selection);
        };
        this.compareMode = true;
        return configuration;
    }

    ngOnInit() {
        super.ngOnInit();
        this.refreshOnce(null);

        this.subscription.add(this.dashboardEventService.explainTimeShiftSubject.subscribe({
            next: (newAsOfDatetime: moment.Moment) => {
                this.refreshOnce(newAsOfDatetime);
            }
        }));
    }

    protected refreshOnce(newAsOfDatetime: moment.Moment) {
        this.panelBlocked = true;
        const startHour = this.appConfig.activeUserProfile.getUserStartTimeHour();
        const endHour = this.appConfig.activeUserProfile.getUserEndTimeHour();
        let startDatetime = moment().startOf('day').subtract(1, 'day').add(startHour, 'hours').utc();
        if (this.appConfig.asOfDateMode) {
            if (this.previousAsOfDatetime) {
                startDatetime = this.previousAsOfDatetime;
            } else {
                startDatetime = this.appConfig.asOfDatetimeUTC;
            }
        } else {
            if (newAsOfDatetime) {
                if (newAsOfDatetime.isSame(startDatetime, 'date')) {
                    this.compareHeader = newAsOfDatetime.clone().local().format('HH:mm');
                } else {
                    this.compareHeader = newAsOfDatetime.clone().local().format('MM/DD HH:mm');
                }
                startDatetime = newAsOfDatetime.clone().utc();
            } else {
                this.compareHeader = startDatetime.clone().local().format('HH:mm');
            }
        }
        const endDatetime = this.appConfig.asOfDateMode ? this.appConfig.asOfDatetimeUTC : moment.utc();
        this.previousAsOfDatetime = endDatetime;
        this.liveHeader = moment().format('HH:mm');

        const startRequest = this.survivalHorizonService.buildPollingRequestParams();
        startRequest.currencies = [this.currency];
        startRequest.maxEventDatetime = generateEventDatetime(startDatetime);
        const currentRequest = this.survivalHorizonService.buildPollingRequestParams();
        currentRequest.currencies = [this.currency];
        currentRequest.maxEventDatetime = generateEventDatetime(endDatetime);

        // Promise.all([
        //     this.awsUtil.webAPICall<GetSurvivalHorizonServiceDataOutput>(WebApiEndpoints.QuerySurvivalHorizonPositions, currentRequest),
        //     this.awsUtil.webAPICall<GetSurvivalHorizonServiceDataOutput>(WebApiEndpoints.QuerySurvivalHorizonPositions, startRequest)
        // ])
        //     .then((pair: GetSurvivalHorizonServiceDataOutput[]) => {


        forkJoin([
            this.survivalHorizonService.getDataObservable(currentRequest).pipe(take(1)),
            this.survivalHorizonService.getDataObservable(startRequest).pipe(take(1)),
        ])
            .pipe(take(1))
            .subscribe({
                next: (pair: GetSurvivalHorizonServiceDataOutput[]) => {
                    const serviceData = pair[0];
                    const startPositions = pair[1].positions;
                    const grouped = serviceData.positions
                        .filter(d => {
                            return (d.currency.toUpperCase() === this.currency.toUpperCase());
                        })
                        .reduce((g, d) => {
                            g.set(d.bucket, d);
                            return g;
                        }, new Map<string, SurvivalHorizonData>());

                    const deltas = startPositions
                        .map(d => {
                            d.rowKey = d.currency + '(Snapshot)_' + d.bucket;
                            const c = grouped.get(d.bucket);
                            const delta = new SurvivalHorizonData();
                            delta.bucket = d.bucket;
                            delta.currency = d.currency;
                            delta.bucketStart = d.bucketStart;
                            delta.bucketEnd = d.bucketEnd;
                            delta.rowKey = d.currency + '(Diff)_' + d.bucket;
                            delta.add(c);
                            delta.subtract(d);
                            return delta;
                        });
                    const allPositions = []
                        .concat(serviceData.positions)
                        .concat(startPositions)
                        .concat(deltas);
                    this.data = allPositions;



                    // Now reduce the data into total breakdown aggregates
                    // const summary = allPositions.reduce((s, d) => {
                    //     s.add(d);
                    //     return s;
                    // }, new SurvivalHorizonData());

                    this.bucketAndShowLadderTableData();
                    this.initializeCharts();
                    // })
                    // .then(() => {
                    //     this.panelBlocked = false;
                    // })
                    // .catch((err) => {
                    //     this.panelBlocked = false;
                    // })

                }, error: (err) => {
                    this.panelBlocked = false;
                }, complete: () => {
                    this.panelBlocked = false;
                }
            })
            ;
    }

    round(value: number): number {
        return CurrencyUtil.round(value, 2);
    }

    getColumnHeader(block: string, blockIndex: number) {
        if (blockIndex === 0) {
            return this.liveHeader;
        } else if (blockIndex === 1) {
            return this.compareHeader;
        } else if (blockIndex === 2) {
            return 'Change';
        }
    }

    getSelectionMode() {
        return 'single';
    }

    protected chartDivName() {
        return 'compareChartDiv';
    }

    tdPositiveCellValue(ccyIndex, amountIndex, value) {
        let temp = 'hot_';
        if (value > 0) {
            if (value > 20) {
                temp += '4';
            } else if (value > 10) {
                temp += '3';
            } else if (value > 5) {
                temp += '2';
            } else if (value > 1) {
                temp += '1';
            }
        }
        return [['positive-cell', 'block_' + ccyIndex, temp, 'amount_' + amountIndex], ['negative-cell']];
    }
    tdNegativeCellValue(ccyIndex, amountIndex, value) {
        let temp = 'cold_';
        if (value < 0) {
            if (value < -20) {
                temp += '4';
            } else if (value < -10) {
                temp += '3';
            } else if (value < -5) {
                temp += '2';
            } else if (value < -1) {
                temp += '1';
            }
        }
        return [['negative-cell', 'block_' + ccyIndex, temp, 'amount_' + amountIndex], ['positive-cell']];
    }
}
